/**
 * ServiceHealthCard Component
 *
 * Displays individual service health status with:
 * - Traffic light indicator (green/yellow/red circle)
 * - Service name and plain English description
 * - Last-checked timestamp in relative format
 * - Grayed-out state for not-yet-monitored services
 * - Expandable diagnostic panel for degraded services (yellow/red)
 */

import { useState, useEffect } from 'react';
import { Card, Badge, Collapse, Button, Card as BSCard } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faClock,
  faQuestionCircle,
  faChevronDown,
  faChevronUp,
  faCopy,
  faCode
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { db } from 'config/firebase';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { getBusinessImpact, generateClaudeCodeContext } from '../config/diagnosticTemplates';
import { MiniUptimeTimeline } from './UptimeTimeline';
import './ServiceHealthCard.css';

const STATUS_CONFIG = {
  green: {
    color: 'success',
    icon: faCheckCircle,
    label: 'Healthy',
    bgClass: 'status-green'
  },
  yellow: {
    color: 'warning',
    icon: faExclamationTriangle,
    label: 'Degraded',
    bgClass: 'status-yellow'
  },
  red: {
    color: 'danger',
    icon: faTimesCircle,
    label: 'Down',
    bgClass: 'status-red'
  },
  dormant: {
    color: 'secondary',
    icon: faClock,
    label: 'Dormant',
    bgClass: 'status-dormant'
  },
  unknown: {
    color: 'secondary',
    icon: faQuestionCircle,
    label: 'Unknown',
    bgClass: 'status-unknown'
  },
  notMonitored: {
    color: 'light',
    icon: faQuestionCircle,
    label: 'Not yet monitored',
    bgClass: 'status-not-monitored'
  }
};

/**
 * Format relative time from timestamp
 * Handles both Firestore Timestamp objects and regular Date/string formats
 */
function formatRelativeTime(timestamp) {
  if (!timestamp) return 'Unknown';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
}

/**
 * ServiceHealthCard - Displays health status for a single service
 *
 * @param {Object} props
 * @param {Object} props.service - Service definition from SERVICE_REGISTRY
 * @param {Object} props.healthData - Health data from Firestore (optional for non-monitored)
 * @param {Object} props.serviceUptime - 30-day uptime data { uptimePercent, worstStatus, data }
 */
export function ServiceHealthCard({ service, healthData, serviceUptime }) {
  // State for expandable diagnostic panel
  const [expanded, setExpanded] = useState(false);
  const [recentErrors, setRecentErrors] = useState([]);
  const [loadingErrors, setLoadingErrors] = useState(false);

  // Determine status: if not monitored, show notMonitored; otherwise use healthData
  const isMonitored = service.monitored;
  const status = !isMonitored
    ? 'notMonitored'
    : (healthData?.status || 'unknown');
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;

  // Determine if service is in degraded state (yellow or red)
  const isDegraded = isMonitored && (status === 'yellow' || status === 'red');

  // Load recent errors on expansion (on-demand to save Firestore reads)
  useEffect(() => {
    if (expanded && isDegraded && recentErrors.length === 0 && !loadingErrors) {
      setLoadingErrors(true);
      // Firestore path: systemHealth/services/{serviceId}/errors/recent
      const errorsRef = collection(db, 'systemHealth', 'services', service.id, 'errors', 'recent');
      const errorsQuery = query(errorsRef, orderBy('timestamp', 'desc'), limit(5));

      getDocs(errorsQuery)
        .then(snapshot => {
          const errors = snapshot.docs.map(doc => doc.data());
          setRecentErrors(errors);
        })
        .catch(err => {
          console.error('Failed to load recent errors:', err);
        })
        .finally(() => setLoadingErrors(false));
    }
  }, [expanded, isDegraded, service.id, recentErrors.length, loadingErrors]);

  // Copy Claude Code context to clipboard
  const handleCopyContext = async () => {
    const contextText = generateClaudeCodeContext(service.id, healthData, recentErrors);
    try {
      await navigator.clipboard.writeText(contextText);
      toast.success('Copied to clipboard', { position: 'bottom-right', autoClose: 2000 });
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  return (
    <Card className={`service-health-card mb-3 ${!isMonitored ? 'not-monitored' : ''}`}>
      <Card.Body className="d-flex align-items-start">
        {/* Traffic light indicator */}
        <div className={`traffic-light ${statusConfig.bgClass} me-3`}>
          <FontAwesomeIcon icon={statusConfig.icon} />
        </div>

        {/* Service info */}
        <div className="flex-grow-1">
          <div className="d-flex justify-content-between align-items-start mb-1">
            <h6 className={`mb-0 ${!isMonitored ? 'text-muted' : ''}`}>
              {service.name}
            </h6>
            <Badge bg={statusConfig.color} className="ms-2">
              {statusConfig.label}
            </Badge>
          </div>

          <p className={`small mb-1 ${!isMonitored ? 'text-muted' : ''}`}>
            {service.description}
          </p>

          {/* Last checked timestamp */}
          {isMonitored && (
            <small className="text-muted d-block mb-2">
              <FontAwesomeIcon icon={faClock} className="me-1" />
              Last checked: {formatRelativeTime(healthData?.checkedAt)}
            </small>
          )}

          {/* Mini uptime timeline (30 days) */}
          {isMonitored && serviceUptime?.data?.length > 0 && (
            <div className="mt-2">
              <MiniUptimeTimeline data={serviceUptime.data} days={30} />
              {serviceUptime.uptimePercent !== null && (
                <small className="text-muted">
                  30-day uptime: <strong>{serviceUptime.uptimePercent}%</strong>
                </small>
              )}
            </div>
          )}

          {!isMonitored && (
            <small className="text-muted fst-italic">
              Coming in Phase 3
            </small>
          )}
        </div>
      </Card.Body>

      {/* Expandable diagnostic panel for degraded services */}
      {isDegraded && (
        <>
          <div className="diagnostic-toggle px-3 pb-2">
            <Button
              variant="link"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              aria-expanded={expanded}
              aria-controls={`diagnostic-${service.id}`}
              className="p-0 text-decoration-none"
            >
              <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} className="me-1" />
              {expanded ? 'Hide Details' : 'Show Details'}
            </Button>
          </div>

          <Collapse in={expanded}>
            <div id={`diagnostic-${service.id}`} className="diagnostic-panel px-3 pb-3">
              {/* What This Means section */}
              <div className="business-impact mb-3">
                <h6 className="text-muted mb-2">
                  <small>What This Means</small>
                </h6>
                <p className="mb-0">
                  {getBusinessImpact(service.id, status)}
                </p>
              </div>

              {/* Claude Code Context section */}
              <BSCard className="claude-context-card bg-light">
                <BSCard.Header className="d-flex justify-content-between align-items-center py-2">
                  <span className="small">
                    <FontAwesomeIcon icon={faCode} className="me-2" />
                    Claude Code Context
                  </span>
                  <Button variant="outline-secondary" size="sm" onClick={handleCopyContext}>
                    <FontAwesomeIcon icon={faCopy} className="me-1" />
                    Copy
                  </Button>
                </BSCard.Header>
                <BSCard.Body className="py-2">
                  <pre className="claude-context-text mb-0 small">
                    {generateClaudeCodeContext(service.id, healthData, recentErrors)}
                  </pre>
                  {loadingErrors && (
                    <small className="text-muted">Loading recent errors...</small>
                  )}
                </BSCard.Body>
              </BSCard>
            </div>
          </Collapse>
        </>
      )}
    </Card>
  );
}

export default ServiceHealthCard;
